interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  username: string;
}

export class TokenService {
  private static readonly ACCESS_TOKEN_KEY = 'gym_access_token';
  private static readonly REFRESH_TOKEN_KEY = 'gym_refresh_token';
  private static readonly TOKEN_EXPIRY_KEY = 'gym_token_expiry';

  // Store tokens after login
  static setTokens(tokenResponse: TokenResponse): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokenResponse.access_token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokenResponse.refresh_token);
    
    // Calculate and store expiry time
    const expiryTime = Date.now() + (tokenResponse.expires_in * 1000);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
  }

  // Get access token
  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  // Get refresh token
  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  // Check if access token exists and is not expired
  static isTokenValid(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    const expiryTime = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiryTime) return false;

    // Check if token is still valid (with 5 minute buffer)
    return Date.now() < (parseInt(expiryTime) - 5 * 60 * 1000);
  }

  // Check if we have a refresh token
  static hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  // Clear all tokens (logout)
  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
  }

  // Update access token (after refresh)
  static updateAccessToken(newToken: string, expiresIn: number): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, newToken);
    
    const expiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
  }

  // Get authorization header value
  static getAuthHeader(): string | null {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : null;
  }
}

export type { TokenResponse }; 